import { useId, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  ConfigProvider,
  Flex,
  Input,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { ROLLBACK_COLORS } from './rollbackColors';

interface RollbackConfirmPanelProps {
  stepTitle: string;
  // the step is part of a signing chain, rolling it back restarts the whole signing
  resetsSigning: boolean;
  loading: boolean;
  onConfirm: (reason: string, notify: boolean) => void;
  onCancel: () => void;
}

// opens under the step that is rolled back
export const RollbackConfirmPanel = ({
  stepTitle,
  resetsSigning,
  loading,
  onConfirm,
  onCancel,
}: RollbackConfirmPanelProps) => {
  const { t } = useTranslation();
  const reasonId = useId();
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(true);

  return (
    // the buttons, the focused text area and the checkbox use the rollback color
    <ConfigProvider theme={{ token: { colorPrimary: ROLLBACK_COLORS.action } }}>
      <Flex
        vertical
        gap={12}
        style={{
          margin: '8px 0 4px 48px',
          padding: 16,
          border: `1px solid ${ROLLBACK_COLORS.panelBorder}`,
          background: ROLLBACK_COLORS.panelBackground,
          borderRadius: 10,
        }}
      >
        <Flex vertical gap={4}>
          <Typography.Text strong style={{ fontSize: 15 }}>
            {t('processRollback.confirmTitle')}
          </Typography.Text>
          <Typography.Text>
            {t('processRollback.confirmDescription', { step: stepTitle })}
          </Typography.Text>
        </Flex>

        {resetsSigning && (
          <Alert
            type="warning"
            showIcon
            message={t('processRollback.signingResetTitle')}
            description={t('processRollback.signingResetWarning')}
          />
        )}

        <Flex vertical gap={6}>
          <label htmlFor={reasonId}>
            <Typography.Text strong>
              {t('processRollback.reason')}
            </Typography.Text>{' '}
            <Typography.Text type="secondary">
              ({t('processRollback.required')})
            </Typography.Text>
          </label>
          <Input.TextArea
            id={reasonId}
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('processRollback.reasonPlaceholder')}
          />
        </Flex>

        <Checkbox
          checked={notify}
          onChange={(e) => setNotify(e.target.checked)}
        >
          {t('processRollback.notify')}
        </Checkbox>

        <Flex justify="end" gap={8} wrap>
          <Button onClick={onCancel} disabled={loading}>
            {t('processRollback.cancel')}
          </Button>
          <Button
            type="primary"
            loading={loading}
            disabled={!reason.trim()}
            onClick={() => onConfirm(reason.trim(), notify)}
          >
            {t('processRollback.confirm')}
          </Button>
        </Flex>
      </Flex>
    </ConfigProvider>
  );
};
